// directive-redirect-application.ts
//
// P7 §7 tests 25-28: applies an ACCEPTED `redirect`/`answer`/`revert`
// directive from the inbox into the running mission. Acceptance (ack after
// durable write) already happened in `directive-inbox.ts`; this SEPARATE step
// runs at a tick boundary only (never mid-attempt, test 25).
//
// Idempotency: `operation_id` = `redirect-apply-<directive_id>` (mirrors
// supervisor-proposal-application.ts) so a crash-then-replay never re-applies
// the same proposed_changes twice (test 26 "applied exactly once").
import { randomUUID } from "node:crypto";
import type { GitRunner } from "./commit-owner.js";
import { commitRedirectTicketFiles } from "./directive-redirect-ticket-commit.js";
import type { GateId } from "./gate-registry.js";
import { invalidateGateOnShaChange, readGateFile } from "./gate-registry.js";
import type { MissionStore } from "./mission-store.js";
import type { PolicyAdapterInput, PolicyVerdict } from "./policy-adapter.js";
import { applyProposal, type ProposedChange } from "./proposal-applier.js";
import type { Ticket } from "./schemas/ticket-schema.js";
import { checkTicketDagForBatch, writeTicketFile } from "./ticket-file-persistence.js";

export interface RedirectDirectivePayload {
  /** Ticket-transaction changes carried by the redirect (already in proposal-applier shape). */
  proposed_changes?: ProposedChange[];
  /** Ticket ids whose scope narrowed enough that a prior "done" no longer counts (test 28). */
  supersede_done_ticket_ids?: string[];
  /** Gates that must be invalidated because this redirect changes what they approved. */
  invalidate_gate_ids?: GateId[];
}

export interface ApplyRedirectDirectiveInput {
  store: MissionStore;
  controlRoot: string;
  /** Repo whose `.avatar/tickets/` gets the amended/created ticket files (F2). */
  repoDir: string;
  directiveId: string;
  tickets: Ticket[];
  ticketStatus: Record<string, string>;
  payload: RedirectDirectivePayload;
  /** Artifact hash gates should be invalidated AGAINST (post-redirect candidate identity). */
  invalidationArtifactHash: string;
  /** Commit path for the ticket files this redirect writes (same tick — see directive-redirect-ticket-commit.ts). */
  gitRunner: GitRunner;
  evaluatePolicy: (input: PolicyAdapterInput) => Promise<PolicyVerdict>;
  missionId: string;
  /** R-25 (D2): dòng cảnh báo chính sách DAG (luật 3-4) — không chặn. */
  notify?: (line: string) => void;
}

export type ApplyRedirectDirectiveResult =
  | {
      ok: true;
      touchedTicketIds: string[];
      invalidatedGates: GateId[];
    }
  | { ok: false; reason: string };

/**
 * Applies one redirect/answer/revert directive's ticket effects as a single
 * transaction (test 26: proposals applied exactly once — stale
 * `expected_revision` on any change rejects the whole batch, same rule as
 * proposal-applier.ts always enforces) then invalidates any gates the
 * directive named (test 27) and marks named done tickets `superseded`
 * (test 28 — narrowed scope means a previously "done" ticket no longer
 * counts toward mission completion).
 */
export async function applyRedirectDirective(
  input: ApplyRedirectDirectiveInput,
): Promise<ApplyRedirectDirectiveResult> {
  const operationId = `redirect-apply-${input.directiveId}`;
  if (input.store.state.results[operationId] !== undefined) {
    // Already applied (crash-then-replay) — never re-apply (test 26).
    return { ok: true, touchedTicketIds: [], invalidatedGates: [] };
  }

  let currentTickets = input.tickets;
  let currentStatus = { ...input.ticketStatus };
  const touched = new Set<string>();
  const pendingWrites: Ticket[] = [];

  const changes = input.payload.proposed_changes ?? [];
  for (const change of changes) {
    const result = applyProposal(currentTickets, currentStatus, change);
    if (!result.ok) {
      return { ok: false, reason: `redirect ${input.directiveId}: ${result.reason}` };
    }
    currentTickets = result.tickets;
    currentStatus = result.statusMap;
    const touchedId = change.op === "create" ? change.new_ticket?.id : change.ticket_id;
    if (touchedId !== undefined) touched.add(touchedId);
    // F2: amend/create must reach `.avatar/tickets/*.json` (the file
    // supervisor-ticket-projection.ts re-reads every tick — dogfood
    // ME-2026-4f0e72 saw the on-disk file stuck at revision 1). Queued here,
    // flushed AFTER every change in the batch validates (applyProposal
    // rejects the WHOLE batch on the first failure — nothing partial is written).
    if (change.op === "create" && change.new_ticket !== undefined) {
      pendingWrites.push(change.new_ticket);
    } else if (change.op === "amend" && touchedId !== undefined) {
      const amended = currentTickets.find((t) => t.id === touchedId);
      if (amended) pendingWrites.push(amended);
    }
  }

  // Scope narrowing: a ticket already "done" is demoted to "superseded" —
  // it must NOT continue to count as done toward mission completion
  // (test 28). Distinct from proposal-applier's own "supersede" op because
  // the caller here names tickets directly (redirect free text -> ticket
  // ids resolved by the controller before this call), not via a
  // ticket_id+expected_revision transaction entry.
  //
  // Một amend ở vòng trên có thể đã ép ticket "done" -> "todo" (guard
  // acceptance của proposal-applier.ts) — đọc `currentStatus` trần thì
  // supersede trượt. Nhưng KHÔNG đọc `input.ticketStatus` trần thay thế
  // (code-reviewer 260922 H-review1): một `op: "cancel"` cùng directive sẽ bị
  // "superseded" đè lên "cancelled". Điều kiện dưới nhận đúng MỘT chuyển dịch
  // "done" (đầu vào) -> "todo" (do guard đặt), còn lại đọc `currentStatus`.
  for (const ticketId of input.payload.supersede_done_ticket_ids ?? []) {
    const forcedTodoByAcceptanceGuard =
      currentStatus[ticketId] === "todo" && input.ticketStatus[ticketId] === "done";
    if (currentStatus[ticketId] === "done" || forcedTodoByAcceptanceGuard) {
      currentStatus[ticketId] = "superseded";
      touched.add(ticketId);
    }
  }

  // R-25 (D2): cùng luật done-path dùng (supervisor-proposal-application.ts)
  // — kiểm DAG của lô SAU KHI áp toàn bộ trong bộ nhớ, TRƯỚC khi ghi bất kỳ
  // file nào. Dep không tồn tại/vòng chặn cả redirect (0 file ghi); thiếu
  // testcase/verification recipe chỉ notify, redirect vẫn áp.
  const dagCheck = checkTicketDagForBatch(currentTickets, input.notify);
  if (!dagCheck.ok) {
    return { ok: false, reason: `redirect ${input.directiveId}: ${dagCheck.reason}` };
  }

  const invalidatedGates: GateId[] = [];
  for (const gate of input.payload.invalidate_gate_ids ?? []) {
    const gates = await readGateFile(input.controlRoot);
    if (gates[gate].status === "approved" || gates[gate].status === "waived") {
      await invalidateGateOnShaChange(input.controlRoot, gate, input.invalidationArtifactHash);
      invalidatedGates.push(gate);
    }
  }

  // Write file → git commit → journal result, same order the done-path uses
  // (supervisor-proposal-application.ts + routeVerifiedDoneOutcome). A crash
  // before the journal commit replays into the early-return guard at the top
  // on the next tick — writeTicketFile overwrites wholesale, so replay never
  // bumps a revision twice. The git commit is what survives the next tick's
  // recoverOwnedCheckout (`git clean -fd`) — an untracked ticket file would
  // not (re-review 2026-09-26, F-B1).
  const removedPaths: string[] = [];
  for (const t of pendingWrites) {
    removedPaths.push(...(await writeTicketFile(input.repoDir, t)).removed);
  }
  const committed = await commitRedirectTicketFiles({
    store: input.store,
    repoDir: input.repoDir,
    gitRunner: input.gitRunner,
    evaluatePolicy: input.evaluatePolicy,
    missionId: input.missionId,
    directiveId: input.directiveId,
    tickets: pendingWrites,
    removedPaths,
  });
  if (!committed.ok) {
    return { ok: false, reason: `redirect ${input.directiveId}: ${committed.reason}` };
  }

  await input.store.commit([
    {
      event_id: `${operationId}-intent`,
      operation_id: operationId,
      delta: {
        kind: "operation_intent",
        operation_id: operationId,
        operation_kind: "redirect_apply",
        params: { directive_id: input.directiveId, change_count: changes.length },
      },
    },
    {
      event_id: `${operationId}-result`,
      operation_id: operationId,
      delta: { kind: "operation_result", operation_id: operationId, outcome: "applied" },
    },
    ...[...touched].map((ticketId) => ({
      event_id: randomUUID(),
      delta: {
        kind: "ticket_state_changed" as const,
        ticket: ticketId,
        status: currentStatus[ticketId] ?? "todo",
      },
    })),
  ]);

  return { ok: true, touchedTicketIds: [...touched], invalidatedGates };
}
